import { useMemo, useState } from "react";
import { Alert, App, Button, Card, Descriptions, Dropdown, Empty, Input, Modal, Progress, Space, Spin, Steps, Tabs, Tag, Typography } from "antd";
import { DownloadOutlined, EllipsisOutlined } from "@ant-design/icons";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Crumbs } from "../components/Crumbs";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AUDIT_FLOW, DEFAULT_CONFIG, SECS, XSEC, calcScores, type AuditStatus } from "@audit-plan/shared";
import { api, download } from "../api/client";
import { invalidateAudit, useAudit, useAudits, useConfig, useQuestions } from "../api/audits";
import { usePeople } from "../api/people";
import { palette } from "../theme";
import { OutcomePill, fmtD, fmtN, fmtWhen } from "../components/pills";
import { PageHead } from "../components/audit/PageHead";
import { ProgrammeTab, ProgrammeTag } from "../components/audit/ProgrammeTab";
import { OriginLabel } from "../components/audit/AuditTable";
import { ScoreRing } from "../components/audit/ScoreRing";
import { AuditFormModal } from "../components/audit/AuditFormModal";
import { ChecklistTab } from "../components/audit/ChecklistTab";
import { PhaseoutModal } from "../components/audit/PhaseoutModal";
import { AuditEvidenceTab, CapTab, HistoryTab, NcTab, ObsTab, ReportTab } from "../components/audit/AuditTabs";
import { useNotify } from "../components/audit/useNotify";
import { NcDrawer } from "../components/nc/NcDrawer";
import { ActionDrawer } from "../components/action/ActionDrawer";
import { useFullModal } from "../components/audit/mobileModal";
import { useIsMobile } from "../components/useIsMobile";

const SEC_COLOR: Record<string, string> = { S: palette.secS, H: palette.secH, E: palette.secE };

export default function AuditPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const en = i18n.language === "en";
  const qc = useQueryClient();
  const n = useNotify();
  const { modal } = App.useApp();
  const people = usePeople();
  const q = useAudit(id);
  const a = q.data;
  const questions = useQuestions(a?.checklistVersionId).data ?? [];
  const cfg = useConfig().data ?? DEFAULT_CONFIG;
  const parent = useAudit(a?.parentAuditId).data;
  const sibling = useAudits({ supplierId: a?.supplierId }, !!a).data ?? [];
  const [sp] = useSearchParams();
  const [tabSel, setTab] = useState<string | null>(sp.get("tab"));
  const [edit, setEdit] = useState(false);
  const [po, setPo] = useState(false);
  const [ncId, setNcId] = useState<string | null>(null);
  const [actId, setActId] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<AuditStatus | null>(null);
  const [note, setNote] = useState("");
  const mobile = useIsMobile();
  const fm = useFullModal();

  const active = useMemo(() => questions.filter((x) => x.active !== false), [questions]);
  const answered = a ? active.filter((x) => (x.section === XSEC ? a.extra[x.id]?.v : a.answers[x.id]?.r)).length : 0;

  const transition = useMutation({
    mutationFn: (v: { to: AuditStatus; note?: string }) => api.post(`/audits/${id}/transition`, v),
    onSuccess: () => {
      invalidateAudit(qc, id);
      setNoteFor(null);
      setNote("");
    },
    onError: n.fail,
  });
  const sameScore = useMutation({
    mutationFn: () => api.post(`/audits/${id}/same-score`),
    onSuccess: () => {
      invalidateAudit(qc, id);
      n.ok(t("st_closed"));
    },
    onError: n.fail,
  });

  if (q.isLoading) return <Spin />;
  if (!a) return <Empty description={q.error ? (q.error as Error).message : t("noResults")} />;

  const sc = a.scores && a.scores.total != null ? a.scores : active.length ? calcScores(active, a.answers, cfg.weights) : a.liveScores;
  const po_ = a.outcome === "phaseout";
  // regole del rating: a audit chiuso vale il valore registrato, prima quello calcolato sulle risposte attuali
  const ratingOverride = a.status === "closed" ? a.ratingOverride : a.liveRating.ratingOverride;
  const ratingReason = a.status === "closed" ? a.ratingReason : a.liveRating.ratingReason;
  const idx = AUDIT_FLOW.indexOf(a.status);
  const hideDate = a.plannedDate == null;
  const prev = sibling
    .filter((x) => x.id !== a.id && x.status === "closed" && (x.actualDate ?? x.plannedDate ?? "") < (a.actualDate ?? a.plannedDate ?? "9999"))
    .sort((x, y) => ((y.actualDate ?? "") > (x.actualDate ?? "") ? 1 : -1))[0];
  const followUps = sibling.filter((x) => x.parentAuditId === a.id);
  const allowed = a.allowed;
  const has = (to: AuditStatus) => allowed.transitions.includes(to);

  type Act = { key: string; label: string; onClick: () => void; type?: "primary" | "default"; danger?: boolean; loading?: boolean; icon?: React.ReactNode };
  const go = (to: AuditStatus, label: string, opts?: { askNote?: boolean; confirm?: string; type?: "primary" | "default"; danger?: boolean }): Act => ({
    key: `${a.status}-${to}`,
    label,
    type: opts?.type ?? "primary",
    danger: opts?.danger,
    loading: transition.isPending && transition.variables?.to === to,
    onClick: () => {
      if (to === "completed" && active.length && answered < active.length) return n.warn(t("allAnswered"));
      if (opts?.askNote) return setNoteFor(to);
      if (opts?.confirm) return modal.confirm({ title: t("confirm"), content: opts.confirm, okText: t("yes"), cancelText: t("cancel"), onOk: () => transition.mutateAsync({ to }) });
      transition.mutate({ to });
    },
  });
  const buttons: Act[] = [];
  if (allowed.edit) buttons.push({ key: "edit", label: t("edit"), type: "default", onClick: () => setEdit(true) });
  if (a.status === "planned" && has("confirmed")) buttons.push(go("confirmed", t("wf_confirm")));
  if (a.status === "confirmed" && has("in_progress")) buttons.push(go("in_progress", t("wf_start")));
  if (a.status === "in_progress" && has("completed")) buttons.push(go("completed", t("wf_complete")));
  if (a.status === "completed") {
    if (has("in_progress")) buttons.push(go("in_progress", en ? "Reopen" : "Riapri", { type: "default", askNote: true }));
    if (has("in_validation")) buttons.push(go("in_validation", t("wf_submit")));
  }
  if (a.status === "in_validation") {
    if (has("in_progress")) buttons.push(go("in_progress", t("wf_reject"), { type: "default", askNote: true }));
    if (has("closed")) buttons.push(go("closed", t("wf_validate"), { confirm: en ? "Validate and publish the report?" : "Validare e pubblicare il report?" }));
  }
  if (allowed.phaseout) buttons.push({ key: "po", label: t("wf_phaseout"), type: "default", danger: true, onClick: () => setPo(true) });
  if (allowed.sameScore && parent?.scores)
    buttons.push({
      key: "same",
      label: t("sameScore"),
      type: "default",
      loading: sameScore.isPending,
      onClick: () =>
        modal.confirm({
          title: t("confirm"),
          content: en ? `Close follow-up ${a.code} with the same score as ${parent.code} (${fmtN(parent.scores?.total, 1)})?` : `Chiudere il follow-up ${a.code} con lo stesso punteggio di ${parent.code} (${fmtN(parent.scores?.total, 1)})?`,
          okText: t("yes"),
          cancelText: t("cancel"),
          onOk: () => sameScore.mutateAsync(),
        }),
    });
  // il Report PDF è quello generato dal tool: gli audit storici non ce l'hanno (i loro documenti sono nelle Evidenze)
  if (!a.historical) buttons.push({ key: "pdf", label: t("exportPdf"), type: "default", icon: <DownloadOutlined />, onClick: () => download(`/audits/${a.id}/report.pdf`) });

  const mainAct = buttons.find((x) => x.type === "primary");
  const headExtra = mobile ? (
    <div style={{ display: "flex", gap: 8, width: "100%" }}>
      {mainAct ? (
        <Button type="primary" size="large" style={{ flex: 1, height: 48, minWidth: 0 }} loading={mainAct.loading} onClick={mainAct.onClick}>
          {mainAct.label}
        </Button>
      ) : null}
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        menu={{
          items: buttons.filter((x) => x !== mainAct).map((x) => ({ key: x.key, label: x.label, danger: x.danger, icon: x.icon, onClick: x.onClick })),
        }}
      >
        <Button size="large" style={{ height: 48, flex: mainAct ? "none" : 1 }} icon={<EllipsisOutlined />}>
          {t("b_actions_menu")}
        </Button>
      </Dropdown>
    </div>
  ) : (
    buttons.map((x) => (
      <Button key={x.key} type={x.type ?? "primary"} danger={x.danger} icon={x.icon} loading={x.loading} onClick={x.onClick}>
        {x.label}
      </Button>
    ))
  );

  const stepsItems = AUDIT_FLOW.map((st, i) => ({
    title: st === "closed" && po_ ? t("phaseout") : t(`st_${st}`),
    status: (po_ && st === "closed" ? "error" : a.status === "closed" || i < idx ? "finish" : i === idx ? "process" : "wait") as "finish" | "process" | "wait" | "error",
    description: st === "planned" ? (hideDate ? "" : fmtWhen(a)) : st === "in_progress" && a.startedAt ? fmtD(a.startedAt) : st === "closed" && a.closedAt ? fmtD(a.closedAt) : "",
  }));

  // audit storico: si apre sulle evidenze (la checklist è vuota)
  const tab = tabSel ?? (a.historical ? "ev" : "chk");
  const tabs = [
    { key: "chk", label: `${t("checklist")} (${answered}/${active.length || "…"})`, children: <ChecklistTab audit={a} questions={questions} config={cfg} versionTitle={undefined} onGoEvidence={() => setTab("ev")} active={tab === "chk"} /> },
    { key: "nc", label: `${t("ncs")} (${a.ncs.length})`, children: <NcTab audit={a} questions={questions} onOpenNc={setNcId} /> },
    { key: "obs", label: `${t("observations")} (${a.observations.length})`, children: <ObsTab audit={a} questions={questions} /> },
    { key: "cap", label: `${t("cap")} (${a.actions.length})`, children: <CapTab audit={a} questions={questions} followUps={followUps} onOpenAction={setActId} /> },
    ...(a.programme.required ? [{ key: "prg", label: <span>{t("pg_tab")}<ProgrammeTag audit={a} /></span>, children: <ProgrammeTab audit={a} /> }] : []),
    { key: "ev", label: `${t("evidence")} (${a.evidence.length})`, children: <AuditEvidenceTab audit={a} onChanged={() => invalidateAudit(qc, a.id)} /> },
    ...(a.historical ? [] : [{ key: "rep", label: t("report"), children: <ReportTab audit={a} scores={sc} override={ratingOverride} reason={ratingReason} onPdf={() => download(`/audits/${a.id}/report.pdf`)} /> }]),
    { key: "log", label: t("history"), children: <HistoryTab audit={a} /> },
  ];

  return (
    <div>
      <PageHead
        title={`${a.code} · ${a.supplierName}`}
        sub={
          <div style={{ display: "grid", gap: 6 }}>
            <Crumbs
              items={[
                { label: t("suppliers"), to: "/suppliers" },
                { label: a.supplierName, to: `/suppliers/${a.supplierId}` },
                { label: `${t("f_factory")} ${a.factoryName}`, to: `/factories/${a.factoryId}?s=${a.supplierId}` },
                { label: a.code },
              ]}
            />
            <Space size={8} wrap>
              <Link to={`/suppliers/${a.supplierId}`}>{a.supplierName}</Link>·
              <Link to={`/factories/${a.factoryId}?s=${a.supplierId}`}>
                {a.factoryName}
                {a.factoryCountry ? ` (${a.factoryCountry})` : ""}
              </Link>
              ·<OriginLabel a={a} />
              {a.historical ? <Tag color="purple" style={{ marginInlineEnd: 0 }}>{t("f_historical")}</Tag> : null}
            </Space>
          </div>
        }
        extra={headExtra}
      />
      {po_ ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={<span><b>{t("phaseout")}</b> · {a.phaseoutReason} — {en ? "audit closed without CAP, supplier flagged for phase-out." : "audit chiuso senza CAP, fornitore marcato in phase-out."}</span>} /> : null}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: mobile ? 12 : 24 } }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(340px,100%),1fr))", gap: mobile ? 16 : 24, alignItems: "center" }}>
          <Descriptions column={1} size="small" labelStyle={{ width: mobile ? 104 : 150 }}>
            <Descriptions.Item label={t("f_factory")}>
              {a.factoryName}
              {a.factoryCode ? <span style={{ fontFamily: "var(--ap-mono)", opacity: 0.6 }}> {a.factoryCode}</span> : null}
              {a.factoryCountry ? <span style={{ opacity: 0.6 }}> · {a.factoryCountry}</span> : null}
            </Descriptions.Item>
            <Descriptions.Item label={t("auditType")}>
              {t(`t_${a.type}`)}
              {parent ? <> · {t("parentAudit")} <Link to={`/audits/${parent.id}`}>{parent.code}</Link></> : null}
            </Descriptions.Item>
            <Descriptions.Item label={t("c_origin")}><OriginLabel a={a} /></Descriptions.Item>
            <Descriptions.Item label={t("manager")}>{people.name(a.managerId)}</Descriptions.Item>
            <Descriptions.Item label={t("auditor")}>{people.name(a.auditorId)}</Descriptions.Item>
            {a.ente ? <Descriptions.Item label={t("ente")}>{a.ente}</Descriptions.Item> : null}
            <Descriptions.Item label={a.plannedDateTo ? t("sa_window") : t("plannedDate")}>{hideDate ? <Typography.Text type="secondary">{t("dateHidden")}</Typography.Text> : fmtWhen(a)}</Descriptions.Item>
            <Descriptions.Item label={t("actualDate")}>{fmtD(a.actualDate)}</Descriptions.Item>
            <Descriptions.Item label={t("prevAudit")}>{prev ? `${fmtD(prev.actualDate ?? prev.plannedDate)} · ${prev.code}` : "—"}</Descriptions.Item>
            {a.participants ? <Descriptions.Item label={t("participants")}><span data-sensitive style={{ whiteSpace: "pre-wrap" }}>{a.participants}</span></Descriptions.Item> : null}
            {a.reportTo ? <Descriptions.Item label={t("reportTo")}><span data-sensitive>{a.reportTo}</span></Descriptions.Item> : null}
            <Descriptions.Item label={t("reviewer")}>{people.name(a.reviewerId ?? a.managerId)}</Descriptions.Item>
            {a.outcome === "pass" || a.outcome === "fail" ? <Descriptions.Item label={t("outcome")}><OutcomePill outcome={a.outcome} /></Descriptions.Item> : null}
            {a.scope ? <Descriptions.Item label={t("scope")}>{a.scope}</Descriptions.Item> : null}
          </Descriptions>
          <div style={{ display: "flex", justifyContent: mobile ? "flex-start" : "flex-end", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
            <ScoreRing value={sc.total} override={ratingOverride} />
            <div style={{ minWidth: mobile ? 0 : 190, flex: mobile ? 1 : undefined, display: "grid", gap: 8 }}>
              {SECS.map((k) => (
                <div key={k}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span>
                      <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: SEC_COLOR[k], marginRight: 6 }} />
                      {t(`sec_${k}`)} <Typography.Text type="secondary">{cfg.weights[k]}%</Typography.Text>
                    </span>
                    <b>{sc[k] == null ? "—" : fmtN(sc[k], 1)}</b>
                  </div>
                  <Progress percent={sc[k] ?? 0} showInfo={false} size="small" strokeColor={SEC_COLOR[k]} />
                </div>
              ))}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {a.status === "closed" ? t("final") : t("preliminary")}
                {a.sameScoreOfId ? ` · ${en ? "same score as" : "stesso punteggio di"} ${parent?.code ?? ""}` : ""}
              </Typography.Text>
            </div>
          </div>
        </div>
      </Card>
      {ratingOverride === "D" && !po_ ? <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t("d_ratingOverrideNote", { reason: ratingReason ?? "" })} /> : null}
      {a.suggestedOutcome && allowed.phaseout ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t("d_suggestedPhaseout")}
          description={a.suggestedOutcome.reason}
          action={<Button danger size="small" onClick={() => setPo(true)}>{t("wf_phaseout")}</Button>}
        />
      ) : null}
      <Card size="small" style={{ marginBottom: 16 }}>
        {mobile ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <Typography.Text type="secondary">{t("b_status_now")}:</Typography.Text>
              <Tag color={po_ ? "red" : "blue"} style={{ margin: 0, fontSize: 13 }}>
                {stepsItems[Math.max(0, idx)]?.title}
              </Tag>
              <Typography.Text type="secondary">
                {Math.max(0, idx) + 1}/{AUDIT_FLOW.length}
              </Typography.Text>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {stepsItems.map((x, i) => (
                <div key={i} style={{ flex: 1, height: 6, borderRadius: 3, background: x.status === "error" ? palette.bad : x.status === "finish" ? palette.ok : x.status === "process" ? palette.primary : palette.hair }} />
              ))}
            </div>
          </div>
        ) : (
          <Steps size="small" items={stepsItems} />
        )}
      </Card>
      <Tabs activeKey={tab} onChange={setTab} items={tabs} destroyInactiveTabPane={false} size={mobile ? "small" : "middle"} tabBarGutter={mobile ? 18 : undefined} />
      <AuditFormModal open={edit} onClose={() => setEdit(false)} audit={a} />
      <PhaseoutModal open={po} onClose={() => setPo(false)} audit={a} questions={questions} />
      <NcDrawer ncId={ncId} onClose={() => setNcId(null)} />
      <ActionDrawer actionId={actId} onClose={() => setActId(null)} />
      <Modal
        {...fm}
        open={!!noteFor}
        title={noteFor === "in_progress" && a.status === "in_validation" ? t("wf_reject") : en ? "Reopen" : "Riapri"}
        onCancel={() => setNoteFor(null)}
        okText={t("confirm")}
        cancelText={t("cancel")}
        confirmLoading={transition.isPending}
        onOk={() => noteFor && transition.mutate({ to: noteFor, note: note.trim() || undefined })}
        destroyOnHidden
      >
        <Typography.Text>{t("comment")}</Typography.Text>
        <Input.TextArea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </Modal>
    </div>
  );
}
